import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import StateObserver, { user } from 'src/StateObserver';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import {
  getStreakBattleRoyaleGoal,
  getStreakBattleRoyaleRemainingTime,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { openStreakBattleRoyaleIntro } from '../streakbattleroyale.sequence';
import gsap from 'gsap';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';
import StreakBattleRoyaleInfoSubPanelV2 from './StreakBattleRoyaleInfoSubPanelV2';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';

type StreakBattleRoyaleInfoPanelOpts = {
  onClose: () => void;
} & ViewOpts;

export default class StreakBattleRoyaleInfoPanelV2 extends View {
  private timer: Match2Timer;
  private level: StreakBattleRoyaleInfoSubPanelV2;
  private survivors: StreakBattleRoyaleInfoSubPanelV2;
  private closeButton!: ButtonScaleView;

  constructor(opts: StreakBattleRoyaleInfoPanelOpts) {
    const width = 677;
    const height = 184;

    super({
      ...opts,
      width,
      height,
      centerOnOrigin: true,
    });

    new ImageScaleView({
      superview: this,
      image: streakBattleRoyaleTheme().getAsset('panel_info'),
      width,
      height,
      x: width * 0.5,
      y: height * 0.5,
      centerOnOrigin: true,
    });

    this.timer = new Match2Timer({
      superview: this,
      x: width * 0.5,
      y: height - 18,
      centerOnOrigin: true,
    });
    this.timer.setRemainingTimeGetter((now) =>
      getStreakBattleRoyaleRemainingTime(user(), now),
    );
    this.timer.autoUpdate();

    this.createTitle();
    this.createMessageLabel();
    this.createInfoButton();
    this.createCloseButton(opts.onClose);

    const offsetX = 210;
    const y = 202;

    this.level = new StreakBattleRoyaleInfoSubPanelV2({
      superview: this,
      x: width * 0.5 - offsetX,
      y,
      type: 'level',
    });

    this.survivors = new StreakBattleRoyaleInfoSubPanelV2({
      superview: this,
      x: width * 0.5 + offsetX,
      y,
      type: 'players',
    });
  }

  private createTitle() {
    const locale = StateObserver.getLocale();
    new ImageScaleView({
      superview: this,
      scaleMethod: 'contain',
      image: streakBattleRoyaleTheme().getAsset(`title_event_${locale}`),
      width: 441,
      height: 84,
      scale: 1,
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: 20,
    });
  }

  private createMessageLabel() {
    new LangBitmapFontTextView({
      superview: this,
      font: 'Body',
      x: this.style.width * 0.5,
      y: this.style.height * 0.5 - 20,
      centerOnOrigin: true,
      localeText: () =>
        i18n('match2.streakBattleRoyale.popup.infoMessage', {
          levels: getStreakBattleRoyaleGoal(user()),
        }),
      align: 'center',
      verticalAlign: 'center',
      color: '#256887',
      size: 27,
    });
  }

  private createInfoButton() {
    new ButtonScaleView({
      ...buttons.info,
      superview: this,
      x: buttons.info.width / 2 - 10,
      y: buttons.info.height / 2 - 16,
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP_INFO,
      },
      onClick: async () => {
        await openStreakBattleRoyaleIntro({ hideClose: true });
      },
    });
  }

  private createCloseButton(onClose: () => void) {
    this.closeButton = new ButtonScaleView({
      ...buttons.close,
      superview: this,
      x: this.style.width - buttons.close.width / 2 + 10,
      y: buttons.close.height / 2 - 16,
      onClick: async () => {
        sounds.playSound('cancel');
        onClose();
      },
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.POPUP_CLOSE,
      },
    });
  }

  public setSurvivors(v: number, animate = false) {
    if (animate) this.survivors.animateValue(v);
    else this.survivors.value = v;
  }

  public setCheckpoint(v: number, animate = false) {
    if (animate) this.level.animateValue(v);
    else this.level.value = v;
  }

  public setCloseButtonVisible(v: boolean) {
    this.closeButton.updateOpts({ visible: v });
  }

  public async hide(instant = false) {
    gsap.to(this.style, {
      duration: instant ? Number.EPSILON : 0.5,
      scale: 0,
      opacity: 0,
      y: this.style.y - this.style.height,
      ease: 'back.in',
      onComplete: () => {
        this.updateOpts({ visible: false });
      },
    });
  }
}
